import request from 'api/requestUtils'
import config from 'constants/config'
import type { paths } from '@luxuryescapes/contract-svc-payment'
import {
  merchantFeeConfigMap,
  merchantFeeDetailsMap,
  payToSupportedBanksMap,
  savedStripeCardsMap,
  securePaymentInfoMap,
  paymentScheduleDataMap,
  paymentScheduleDetailsMap,
  getPaymentDetailsForOrdersMap,
  createPaymentRequestMap,
  adjustPaymentScheduleMap,
  getPayPalSettingsMap,
  paymentTransactionDetailsMap,
  dynamicPaymentScheduleDataMap,
  luxPlusOffSessionCardsByRegionAndPurposeIdMap,
} from './mappers/paymentMap'

export function createRazorpayOrder(data: any) {
  return request.post<any, any>('/api/razorpay/create_order', data, { credentials: 'include' })
}

export function getDepositDetails(orderId: string): Promise<App.Deposit | undefined> {
  return request.get(`/api/payments/deposit-details/${orderId}`, { credentials: 'include' })
    .then((data: any | null) => {
      return data ? {
        ...data,
        paid_amount: parseFloat(data.paid_amount),
        balance_amount: parseFloat(data.balance_amount),
        total_amount: parseFloat(data.total_amount),
      } : undefined
    })
}

export function createPaymentPlanForDepositBalance(data: any) {
  return request.post<any, any>('/api/payment_plan/deposit-balance', data, { credentials: 'include' })
}

export function createDepositPaymentPlanForOrderId(data: any) {
  return request.post<any, any>('/api/payment_plan/balance-deposit', data, { credentials: 'include' })
}

export function getInstalmentDetails(orderId: string): Promise<App.InstalmentDetails> {
  return request.get(`/api/payments/instalment-details/${orderId}`, { credentials: 'include' }) as any
}

export type ValidateRebookingResponse = paths['/api/payments/v2/payment-schedules/validate-rebooking/{orderId}']['get']['responses']['200']['content']['application/json']
export type PaymentScheduleRebookingValidationResult = ValidateRebookingResponse['result']

export function validatePaymentScheduleRebooking(orderId: string): Promise<PaymentScheduleRebookingValidationResult> {
  return request.get<ValidateRebookingResponse>(
    `/api/payments/v2/payment-schedules/validate-rebooking/${orderId}`,
    { credentials: 'include' },
  ).then(response => response.result)
}

export function createInstalmentPaymentPlanForOrderId(data: any) {
  return request.post<any, any>('/api/payment_plan/balance-instalment', data, { credentials: 'include' })
}

export function getReserveForZeroDetails(orderId: string): Promise<App.ReserveForZeroDetails | undefined> {
  return request.get<any | null>(`/api/payments/deferred-payment-details/${orderId}`, { credentials: 'include' }).then(response => {
    if (response) {
      return {
        bookingStatus: response.booking_status,
        currency: response.currency,
        orderIds: response.fk_orders,
        isActive: response.is_active,
        payableAmount: Number(response.payable_amount),
        schedulePaymentDate: response.scheduled_payment_date,
        actualPaymentDate: response.actual_payment_date,
      }
    }
  })
}

export function createDeferredPaymentPlanForOrderId(data: any) {
  return request.post<any, any>('/api/payment_plan/deferred-payment', data, { credentials: 'include' })
}

export function createPaymentSchedulePaymentPlanForOrderId(data: { orderId: string, payments: Array<App.PaymentInfo>, chargeIds?: Array<string> }): Promise<any> {
  return request.post<any, any>('/api/payments/v2/payment-schedules/balance', data, { credentials: 'include' })
}

export function getPaymentPlanGroup(payment_plan_group_id: string): Promise<App.ReserveForZeroDetails | undefined> {
  return request.get<any | null>(`/api/payment_plan/${payment_plan_group_id}`, { credentials: 'include' }).then(response => {
    if (response) {
      const paymentPlanGroup: App.ReserveForZeroDetails = {
        bookingStatus: response.booking_status,
        currency: response.currency,
        orderIds: response.fk_orders,
        isActive: response.is_active,
        payableAmount: response.payable_amount,
        schedulePaymentDate: response.schedule_payment_date,
        actualPaymentDate: response.actual_payment_date,
      }
      return paymentPlanGroup
    }
  })
}

export type StripeSavedCardsV2Response = paths['/api/payments/v2/stripe/payment-methods/saved-cards']['get']['responses']['200']['content']['application/json']

export async function getSavedStripeCards(currency: string) {
  return request.get<StripeSavedCardsV2Response>(`/api/payments/v2/stripe/payment-methods/saved-cards?currency=${currency}`, { credentials: 'include' })
    .then(response => savedStripeCardsMap(response.result))
}

export async function getAgentHubAgencySavedStripeCards(currency: string) {
  const params = new URLSearchParams({
    currency,
  })

  return request.get<StripeSavedCardsV2Response>(`/api/payments/v2/stripe/payment-methods/agency/saved-cards?${params.toString()}`, { credentials: 'include' })
    .then(response => savedStripeCardsMap(response.result))
}

interface GoCardlessBillingData {
  email: string
  givenName: string
  lastName: string
  amount: number
  currency: string
  description?: string
  orderId: string
  transactionKey?: string
}

interface GoCardlessBillingPostData {
  amount: number
  currency: string
  description?: string
  customer: {
    email: string
    givenName: string
    familyName: string
  }
  bankDetails?: any
  redirectUri?: string
  exitUri?: string
  metadata: {
    orderId: string
    transactionKey?: string
  }
}

export function createGoCardlessBillingRequest(data: GoCardlessBillingData): Promise<string> {
  const postData: GoCardlessBillingPostData = {
    amount: data.amount,
    currency: data.currency,
    metadata: {
      orderId: data.orderId,
      transactionKey: data.transactionKey,
    },
    description: data.description,
    customer: {
      email: data.email,
      familyName: data.lastName,
      givenName: data.givenName,
    },
  }
  return request.post<App.ApiResponse<{ flowId: string }>, GoCardlessBillingPostData>(
    '/api/payments/v2/gocardless/billing-requests',
    postData,
    { credentials: 'include' },
  ).then(response => {
    return response.result.flowId
  })
}

export type MerchantFeeConfigResponse = paths['/api/payments/v2/merchant-fees/config']['get']['responses']['200']['content']['application/json']

export async function getMerchantFeeConfig(utmMedium: string): Promise<Array<App.MerchantFeeConfig>> {
  const params = new URLSearchParams()
  if (utmMedium) {
    params.set('utm_medium', utmMedium)
  }

  const query = params.toString()
  const url = `/api/payments/v2/merchant-fees/config${query ? `?${query}` : ''}`

  return request.get<MerchantFeeConfigResponse>(url, { credentials: 'include' })
    .then(response => merchantFeeConfigMap(response.result))
}

export type MerchantFeeDetailsResponse = paths['/api/payments/v2/merchant-fees/orders/{orderId}/details']['get']['responses']['200']['content']['application/json']

export async function getMerchantFeeDetails(orderId: string): Promise<Array<App.MerchantFeeDetails>> {
  return request.get<MerchantFeeDetailsResponse>(`/api/payments/v2/merchant-fees/orders/${orderId}/details`, { credentials: 'include' })
    .then(response => merchantFeeDetailsMap(response.result))
}

export type PaymentEventLogRequest = paths['/api/payments/v2/frontend-event-logger/events']['post']['requestBody']['content']['application/json']

export async function postPaymentEventLog(data: PaymentEventLogRequest): Promise<void> {
  return request.post<void, PaymentEventLogRequest>('/api/payments/v2/frontend-event-logger/events', data, { credentials: 'include' })
}

export type PayToSupportedBanksResponse = paths['/api/payments/v2/stripe/payto/supported-banks']['get']['responses']['200']['content']['application/json']

export async function getPayToSupportedBanks(): Promise<Array<App.PayToSupportedBank>> {
  return request.get<PayToSupportedBanksResponse>('/api/payments/v2/stripe/payto/supported-banks', { credentials: 'include' })
    .then(response => payToSupportedBanksMap(response.result))
}

export type GetSavedCardV2Response = paths['/api/payments/v2/stripe/payment-methods/saved-cards/{id}']['get']['responses']['200']['content']['application/json']

export function getSavedCardV2(cardId: string, currency: string, region: string) {
  return request.get<GetSavedCardV2Response>(`/api/payments/v2/stripe/payment-methods/saved-cards/${cardId}?currency=${currency}&region=${region}`, {
    credentials: 'include',
  }).then((response) => {
    return response.result
  })
}

export function deleteSavedStripeCardV2(cardId: string, currency: string, region: string) {
  return request.delete(`/api/payments/v2/stripe/payment-methods/saved-cards/${cardId}?currency=${currency}&region=${region}`, {
    credentials: 'include',
  })
}

export function deleteSavedStripeCard(customerId: string, cardId: string, currency: string) {
  return request.post(`/api/stripe/${customerId}/delete-stripe-card`, {
    card_id: cardId,
    currency,
  }, {
    credentials: 'include',
  })
}

export type SherlogRequest = paths['/api/payments/v2/sherlog/log-requests']['post']['requestBody']['content']['application/json']

export async function postSherLogRequest(data: SherlogRequest): Promise<void> {
  return request.post<void, SherlogRequest>('/api/payments/v2/sherlog/log-requests', data, { credentials: 'include' })
}

export type DepositConfigResponse = paths['/api/payments/v2/payment-config/deposit']['get']['responses']['200']['content']['application/json']

export async function getDepositConfig(): Promise<App.DepositConfig> {
  return request
    .get<DepositConfigResponse>('/api/payments/v2/payment-config/deposit', {
      credentials: 'include',
    })
    .then((response) => {
      return response.result
    })
}

export type GetOffSessionPaymentMethodByRegionAndPurposeIdResponse = paths['/api/payments/v2/off-session-payment-methods/region/{region}/purpose/{purposeId}']['get']['responses']['200']['content']['application/json']

export type LuxPlusSubscriptionRenewalPaymentCards = App.LuxPlusSubscriptionRenewalPaymentCards
export type LuxPlusOffSessionPaymentCard = App.LuxPlusSubscriptionRenewalPaymentCard
export type LuxPlusOffSessionCardsByRegionAndPurposeId = LuxPlusSubscriptionRenewalPaymentCards

export async function getOffSessionPaymentMethodsByRegionAndPurposeId(region: string, purposeId: string): Promise<LuxPlusSubscriptionRenewalPaymentCards> {
  return request.get<GetOffSessionPaymentMethodByRegionAndPurposeIdResponse>(`/api/payments/v2/off-session-payment-methods/region/${region}/purpose/${purposeId}`, { credentials: 'include' })
    .then(response => luxPlusOffSessionCardsByRegionAndPurposeIdMap(response.result))
}

type UpdateStripeOffSessionCardByRegionAndPurposeIdResponse = paths['/api/payments/v2/stripe/off-session-cards/region/{region}/purpose/{purposeId}']['post']['responses']['200']['content']['application/json']
interface UpdateOffSessionCardPayload {
  currency: string
  brand: string
  paymentMethodType: string
  metadata: {
    paymentMethodId: string
    purpose: string
    shouldAppendBackupOffSessionCard?: boolean
  }
}
export async function updateStripeOffSessionCardByRegionAndPurposeId(region: string, purposeId: string, data: UpdateOffSessionCardPayload): Promise<{ clientSecret: string, customerID: string }> {
  return request.post<UpdateStripeOffSessionCardByRegionAndPurposeIdResponse, UpdateOffSessionCardPayload>(`/api/payments/v2/stripe/off-session-cards/region/${region}/purpose/${purposeId}`, data, { credentials: 'include' })
    .then((response) => {
      return response.result
    })
}

type ReorderOffSessionCardsResponse = paths['/api/payments/v2/stripe/off-session-cards/region/{region}/purpose/{purposeId}/reorder']['patch']['responses']['200']['content']['application/json']
type RemoveOffSessionCardResponse = paths['/api/payments/v2/stripe/off-session-cards/region/{region}/purpose/{purposeId}/cards/{cardId}']['delete']['responses']['200']['content']['application/json']
type PromoteOffSessionCardToPrimaryResponse = paths['/api/payments/v2/stripe/off-session-cards/region/{region}/purpose/{purposeId}/cards/{cardId}/primary']['patch']['responses']['200']['content']['application/json']

interface ReorderOffSessionCardsPayload {
  offSessionCardIds: Array<string>
}

export async function updateStripeOffSessionCardPrioritiesByRegionAndPurposeId(region: string, purposeId: string, offSessionCardIds: Array<string>): Promise<void> {
  const payload: ReorderOffSessionCardsPayload = {
    offSessionCardIds,
  }

  await request.patch<ReorderOffSessionCardsResponse, ReorderOffSessionCardsPayload>(
    `/api/payments/v2/stripe/off-session-cards/region/${region}/purpose/${purposeId}/reorder`,
    payload,
    { credentials: 'include' },
  )
}

export async function removeStripeOffSessionCardByRegionAndPurposeId(region: string, purposeId: string, cardId: string): Promise<void> {
  await request.delete<RemoveOffSessionCardResponse>(
    `/api/payments/v2/stripe/off-session-cards/region/${region}/purpose/${purposeId}/cards/${cardId}`,
    { credentials: 'include' },
  )
}

export async function promoteStripeOffSessionCardToPrimaryByRegionAndPurposeId(region: string, purposeId: string, cardId: string): Promise<void> {
  await request.patch<PromoteOffSessionCardToPrimaryResponse, Record<string, never>>(
    `/api/payments/v2/stripe/off-session-cards/region/${region}/purpose/${purposeId}/cards/${cardId}/primary`,
    {},
    { credentials: 'include' },
  )
}

export type CreatePaymentLinkPayload = paths['/api/payments/v2/secure-payments']['post']['requestBody']['content']['application/json']
type CreatePaymentLinkResponse = paths['/api/payments/v2/secure-payments']['post']['responses']['200']['content']['application/json']

export async function createPaymentLink(payload: CreatePaymentLinkPayload): Promise<string> {
  return request.post<CreatePaymentLinkResponse, CreatePaymentLinkPayload>('/api/payments/v2/secure-payments', payload, { credentials: 'include' })
    .then((response) => {
      return response.result.paymentLink
    })
}
interface flyingBluePaymentRequest {
  amount: number
  currency: string
  orderId: string
  brand: string
  redirect_url: string
}

type flyingBluePaymentResponse = paths['/api/payments/v2/flyingblue/payment']['post']['responses']['200']['content']['application/json']
export type flyingBluePaymentResponseData = paths['/api/payments/v2/flyingblue/payment']['post']['responses']['200']['content']['application/json']['result']

export async function postFlyingBluePaymentRequest(data: flyingBluePaymentRequest): Promise<flyingBluePaymentResponseData> {
  return request.post<flyingBluePaymentResponse, flyingBluePaymentRequest>('/api/payments/v2/flyingblue/payment', data, { credentials: 'include' })
    .then((response) => {
      return response.result
    })
}

interface AzupayPayIdPaymentRequest {
  amount: number
  currency: string
  orderId: string
  brand: string
  transactionKey: string
  smsMessageTravellerName?: string
  sendToMobileNumber: string
}
type AzupayPayIdPaymentResponse = paths['/api/payments/v2/azupay/payid/payment']['post']['responses']['200']['content']['application/json']

export async function postAzupayPayIdPaymentRequest(data: AzupayPayIdPaymentRequest): Promise<string> {
  return request.post<AzupayPayIdPaymentResponse, AzupayPayIdPaymentRequest>('/api/payments/v2/azupay/payid/payment', data, { credentials: 'include' })
    .then((response) => {
      return response.result.payID
    })
}

interface AzuPayResendSmsRequest {
  brand: string
  orderId: string
  transactionKey: string
}

export async function postAzuPayResendSmsRequest(data: AzuPayResendSmsRequest) {
  return request.post<void, AzuPayResendSmsRequest>('/api/payments/v2/azupay/payid/resend-sms', data, { credentials: 'include' })
}

export type GetSecurePaymentInfoResponse = paths['/api/payments/v2/secure-payments/payment-link-data/{id}']['get']['responses']['200']['content']['application/json']
export interface SecurePaymentInfo {
  type: 'order' | 'credit'
  amount: number
  currency: string
  orderId?: string
  customerId: string
  brand: string
  isDeposit: boolean
  isDepositBalance: boolean
  isPaid: boolean
  isExpired: boolean
  hasLuxPlus: boolean
  items: Array<{
    total: number
    type: string
  }>
  promoAmount: number
  creditAmount: number
  balanceAmount: number | undefined
  balanceDueDate: string | undefined
  metadata?: {
    itemNames?: Array<string>
    disableCreditPurchaseMerchantFees?: boolean
  }
}

export async function getSecurePaymentInfo(paymentLinkId: string): Promise<SecurePaymentInfo> {
  return request.get<GetSecurePaymentInfoResponse>(`/api/payments/v2/secure-payments/payment-link-data/${paymentLinkId}`, { credentials: 'include' })
    .then((response) => {
      return securePaymentInfoMap(response.result)
    })
}

export type PaymentScheduleResponse = paths['/api/payments/v2/payment-schedules']['post']['responses']['200']['content']['application/json']

export type PaymentScheduleIneligibilityReason = PaymentScheduleResponse['result']['ineligibility_reason']

export interface PaymentScheduleResponseData {
  eligible: boolean
  ineligibilityReason?: PaymentScheduleIneligibilityReason
  selectedTemplateId?: string
  charges?: Array<{
    id: string
    paymentType: string
    dueDate: string
    baseAmount: number
    feeAmount: number
    totalAmount: number
    creditApplied: number
    expectedAmount: number
    retry?: {
      maxChargeAttempts: number
    }
    fee?: {
      type: 'percentage' | 'fixed_amount'
      value: number
      refundable: boolean
    }
    eligibleItem?: {
      totalAmount: number
    }
    itemsBreakdown?: Array<{
      itemId: string
      totalAmount: number
      type: string
    }>
  }>
}
export interface PaymentScheduleBookingInfo {
  booking_date: string
  checkin_date: string
  supplier_due_date?: string
  is_credit_applied?: boolean
  is_gift_card_applied?: boolean
  gift_card_amount?: number
  is_payment_schedule_rebooking?: boolean
  service_fee_destination?: 'DOMESTIC' | 'INTERNATIONAL'
  currency: string
  items: Array<{
    id_items: string
    total: number
    type: string
    discount_total: number
    offer_type?: string
    isPaymentScheduleSupported?: boolean
  }>
}

export interface CustomOfferPreview {
  deposit_amount: number
  balance_amount: number
  balance_due_date: string
  currency?: string
  is_credit_applied?: boolean
  gift_card_amount?: number
}

export interface PaymentScheduleRequest {
  template_id?: string
  template_ids?: Array<string>
  booking_info?: PaymentScheduleBookingInfo
  custom_offer_preview?: CustomOfferPreview
  brand?: string
  groupPayment?: App.Checkout.GroupPaymentAllocation
}

export async function postPaymentScheduleRequest(data: PaymentScheduleRequest): Promise<PaymentScheduleResponseData> {
  return request.post<PaymentScheduleResponse, PaymentScheduleRequest>('/api/payments/v2/payment-schedules', data, { credentials: 'include' })
    .then((response) => {
      return paymentScheduleDataMap(response.result)
    })
}

export async function postGroupPaymentPreviewRequest(
  bookingInfo: App.Checkout.PaymentScheduleRequestInfo['bookingInfo'],
  allocation: App.Checkout.GroupPaymentAllocation,
): Promise<App.Checkout.GroupPaymentPreviewResult> {
  const data: PaymentScheduleRequest = {
    brand: config.BRAND,
    booking_info: {
      booking_date: bookingInfo.bookingDate,
      checkin_date: bookingInfo.checkinDate,
      supplier_due_date: bookingInfo.supplierDueDate,
      is_credit_applied: false,
      is_gift_card_applied: false,
      gift_card_amount: 0,
      is_payment_schedule_rebooking: false,
      service_fee_destination: bookingInfo.serviceFeeDestination,
      currency: bookingInfo.currency,
      items: bookingInfo.items.map(item => ({
        id_items: item.idItems,
        total: item.total,
        type: item.type,
        discount_total: item.discountTotal,
        offer_type: item.offerType,
        ...(item.isPaymentScheduleSupported && { isPaymentScheduleSupported: true }),
      })),
    },
    groupPayment: allocation,
  }

  return request.post<PaymentScheduleResponse, PaymentScheduleRequest>(
    '/api/payments/v2/payment-schedules',
    data,
    { credentials: 'include' },
  ).then((response) => {
    const paymentSchedule = paymentScheduleDataMap(response.result)
    const summary = response.result.group_payment

    return {
      eligible: paymentSchedule.eligible,
      ineligibilityReason: paymentSchedule.ineligibilityReason,
      charges: paymentSchedule.charges,
      ...(summary && {
        summary: {
          allocatableAmount: summary.allocatable_amount,
          leadContributionAmount: summary.lead_contribution_amount,
          participantContributionAmount: summary.participant_contribution_amount,
        },
      }),
    }
  })
}

export type PayGroupPaymentContributionResponse =
  paths['/api/payments/v2/group-payments/contributions/{contributionId}/pay']['post']['responses']['200']['content']['application/json']
export type PayGroupPaymentContributionRequest =
  paths['/api/payments/v2/group-payments/contributions/{contributionId}/pay']['post']['requestBody']['content']['application/json']

export function createGroupPaymentContributionPaymentPlanForContributionId(
  data: { contributionId: string, payments: Array<App.PaymentInfo> },
): Promise<void> {
  return request.post<PayGroupPaymentContributionResponse, PayGroupPaymentContributionRequest>(
    `/api/payments/v2/group-payments/contributions/${data.contributionId}/pay`,
    { payments: data.payments } as PayGroupPaymentContributionRequest,
    { credentials: 'include' },
  ).then(() => undefined)
}

export interface DynamicPaymentScheduleRequest {
  template_id: string
  booking_info: PaymentScheduleBookingInfo
}

export type DynamicPaymentScheduleResponse = paths['/api/payments/v2/payment-schedules/dynamic']['post']['responses']['200']['content']['application/json']

export async function postDynamicPaymentScheduleRequest(data: DynamicPaymentScheduleRequest): Promise<App.DynamicPaymentScheduleChargeData> {
  return request.post<DynamicPaymentScheduleResponse, DynamicPaymentScheduleRequest>('/api/payments/v2/payment-schedules/dynamic', data, { credentials: 'include' })
    .then((response) => {
      return dynamicPaymentScheduleDataMap(response.result)
    })
}

export type PaymentScheduleDetailsResponse = paths['/api/payments/v2/payment-schedules-details/{orderId}']['get']['responses']['200']['content']['application/json']

export async function getPaymentScheduleDetails(orderId: string): Promise<App.PaymentScheduleDetails | undefined> {
  return request.get<PaymentScheduleDetailsResponse>(`/api/payments/v2/payment-schedules-details/${orderId}`, { credentials: 'include' })
    .then((response) => {
      return paymentScheduleDetailsMap(response.result)
    })
}

export type PaymentDetailsForOrdersResponse = paths['/api/payments/v2/payments']['get']['responses']['200']['content']['application/json']

export async function getPaymentDetailsForOrders(orderIds: Array<string>) {
  // Local checkout often runs against a local svc-order while svc-payment stays remote.
  // Orders created locally do not exist in remote payments, so this hydration request
  // would fail after checkout and block developers from reaching the confirmation page.
  if (config.APP_ENV === 'development' && config.SKIP_PAYMENT_DETAILS_FOR_ORDERS) {
    return {}
  }

  const params = new URLSearchParams()
  params.set('orderIds', orderIds.join(','))
  return request.get<PaymentDetailsForOrdersResponse>(`/api/payments/v2/payments?${params.toString()}`, { credentials: 'include' })
    .then((response) => {
      return getPaymentDetailsForOrdersMap(response.result)
    })
}

export interface PaymentDetails {
  type: string
  status: string
  gift_card_number?: string
  amount: string
  currency: string
}

interface PaymentTransactionResponse {
  payments: Array<PaymentDetails>
}

export async function getPaymentDetailsForTransaction(transactionKey: string): Promise<Array<App.PaymentTransactionDetail>> {
  return request.get<PaymentTransactionResponse>(`/api/payments/transaction/${transactionKey}`, { credentials: 'include' })
    .then((response: PaymentTransactionResponse) => {
      return paymentTransactionDetailsMap(response.payments)
    })
}

type PayPalCredentialsResponse = paths['/api/payments/v2/paypal/credentials']['get']['responses']['200']['content']['application/json']
type PayPalSettingsResponse = paths['/api/payments/v2/paypal/settings']['get']['responses']['200']['content']['application/json']
export type PayPalSettings = paths['/api/payments/v2/paypal/settings']['get']['responses']['200']['content']['application/json']

export async function getPayPalCredentials(currency: string) {
  return request.get<PayPalCredentialsResponse>(`/api/payments/v2/paypal/credentials?currency=${currency}`, { credentials: 'include' })
    .then((response) => {
      return response.result
    })
}

export async function getPayPalSettings(): Promise<App.PayPalSettings> {
  return request.get<PayPalSettingsResponse>('/api/payments/v2/paypal/settings', { credentials: 'include' })
    .then((response) => {
      return getPayPalSettingsMap(response.result)
    })
}

export type CreatePaymentRequest = paths['/api/payments/v2/payment-requests']['post']['requestBody']['content']['application/json']
export type CreatePaymentRequestResponse = paths['/api/payments/v2/payment-requests']['post']['responses']['200']['content']['application/json']

interface PayPalOrderPaymentRequest {
  paymentRequestType: 'order'
  id: string
}

interface PayPalVaultSetupPaymentRequest {
  paymentRequestType: 'vault_setup'
  setupToken: string
}

export interface PaymentRequest {
  paypal?: PayPalOrderPaymentRequest | PayPalVaultSetupPaymentRequest
}

export async function createPaymentRequest(data: CreatePaymentRequest): Promise<PaymentRequest> {
  return request.post<CreatePaymentRequestResponse, CreatePaymentRequest>('/api/payments/v2/payment-requests', data, { credentials: 'include' })
    .then((response) => {
      return createPaymentRequestMap(response.result)
    })
}

interface CapturePayPalOrderParams {
  currency: string
  region: string
  orderId: string
  brand: string
  paypalOrderId: string
}

type CapturePayPalOrderRequest = paths['/api/payments/v2/paypal/orders/{paypalOrderId}/capture']['post']['requestBody']['content']['application/json']

export async function capturePayPalOrder(params: CapturePayPalOrderParams) {
  const { paypalOrderId } = params
  const data = {
    currency: params.currency as CapturePayPalOrderRequest['currency'],
    region: params.region as CapturePayPalOrderRequest['region'],
    brand: params.brand as CapturePayPalOrderRequest['brand'],
    orderId: params.orderId,
  }
  return request.post<void, CapturePayPalOrderRequest>(`/api/payments/v2/paypal/orders/${paypalOrderId}/capture`, data, { credentials: 'include' })
}

export type CreatePayPalVaultPaymentTokenRequest = paths['/api/payments/v2/paypal/vault/payment-tokens']['post']['requestBody']['content']['application/json']

interface CreatePayPalVaultPaymentTokenParams {
  currency: string
  region: string
  orderId: string
  setupTokenId: string
}

export async function createPayPalVaultPaymentToken(params: CreatePayPalVaultPaymentTokenParams) {
  const data: CreatePayPalVaultPaymentTokenRequest = {
    currency: params.currency as CreatePayPalVaultPaymentTokenRequest['currency'],
    region: params.region as CreatePayPalVaultPaymentTokenRequest['region'],
    brand: config.BRAND as CreatePayPalVaultPaymentTokenRequest['brand'],
    orderId: params.orderId,
    setupTokenId: params.setupTokenId,
  }
  return request.post<void, CreatePayPalVaultPaymentTokenRequest>('/api/payments/v2/paypal/vault/payment-tokens', data, { credentials: 'include' })
}

export type ProcessTerminalPaymentRequest = paths['/api/payments/v2/stripe/terminal/process-payment']['post']['requestBody']['content']['application/json']

export type ProcessTerminalPaymentResponse = paths['/api/payments/v2/stripe/terminal/process-payment']['post']['responses']['200']['content']['application/json']

export async function processTerminalPayment(data: ProcessTerminalPaymentRequest): Promise<ProcessTerminalPaymentResponse> {
  return request.post<ProcessTerminalPaymentResponse, ProcessTerminalPaymentRequest>('/api/payments/v2/stripe/terminal/process-payment', data, { credentials: 'include' })
}

export type GetTerminalReadersResponse = paths['/api/payments/v2/stripe/terminal/readers']['get']['responses']['200']['content']['application/json']

export async function getTerminalReaders(storeLocation: string, currency: string) {
  const params = new URLSearchParams({
    storeLocation,
    currency,
  })
  return request.get<GetTerminalReadersResponse>(
    `/api/payments/v2/stripe/terminal/readers?${params.toString()}`,
    { credentials: 'include' },
  ).then(response => response.result)
}

export type ProcessTerminalSetupIntentRequest = paths['/api/payments/v2/stripe/terminal/process-setup-intent']['post']['requestBody']['content']['application/json']

export type ProcessTerminalSetupIntentResponse = paths['/api/payments/v2/stripe/terminal/process-setup-intent']['post']['responses']['200']['content']['application/json']

export async function processTerminalSetupIntent(data: ProcessTerminalSetupIntentRequest): Promise<ProcessTerminalSetupIntentResponse> {
  return request.post<ProcessTerminalSetupIntentResponse, ProcessTerminalSetupIntentRequest>(
    '/api/payments/v2/stripe/terminal/process-setup-intent',
    data,
    { credentials: 'include' },
  )
}

export interface AdjustPaymentScheduleRequest {
  order_id: string
  booking_info: {
    checkin_date: string
    currency: string
    id_items: string
    adjustment_amount: number
    all_items_checkin_dates?: Array<{ id_items: string, checkin_date: string }>
  }
}

export interface AdjustPaymentScheduleResponseData {
  eligible: boolean
  adjustedCharges: Array<App.AdjustedPaymentSchedule>
  adjustedSummary: App.AdjustPaymentScheduleSummary
  isDynamicTemplate: boolean
  frequency: 'weekly' | 'fortnightly' | 'monthly' | undefined
}

export type AdjustPaymentScheduleResponse = paths['/api/payments/v2/payment-schedules/adjust']['post']['responses']['200']['content']['application/json']

export async function postAdjustPaymentScheduleRequest(data: AdjustPaymentScheduleRequest): Promise<AdjustPaymentScheduleResponseData> {
  return request.post<AdjustPaymentScheduleResponse, AdjustPaymentScheduleRequest>('/api/payments/v2/payment-schedules/adjust', data, { credentials: 'include' })
    .then((response) => {
      return adjustPaymentScheduleMap(response.result)
    })
}

type ValidateAndFetchGiftCardBalanceResponse = paths['/api/payments/v2/gift-card/balances']['post']['responses']['200']['content']['application/json']

export function validateAndFetchGiftCardBalance(cardNumber: string, region: string, cardPin?: string) {
  const url = `/api/payments/v2/gift-card/balances?region=${region}`
  return request.post<ValidateAndFetchGiftCardBalanceResponse, { cardNumber: string, cardPin?: string }>(url, { cardNumber, ...(cardPin ? { cardPin } : {}) }, { credentials: 'include' })
}

type CancelPendingPayment = paths['/api/payments/v2/payments/cancel/{orderId}']['post']['parameters']['path']

export async function cancelPendingPayment(orderId: string) {
  return request.post<void, CancelPendingPayment>(`/api/payments/v2/payments/cancel/${orderId}`, { orderId }, { credentials: 'include' })
}

export type CancelTerminalPaymentRequest = paths['/api/payments/v2/stripe/terminal/cancel-payment']['post']['requestBody']['content']['application/json']

export type CancelTerminalPaymentResponse = paths['/api/payments/v2/stripe/terminal/cancel-payment']['post']['responses']['200']['content']['application/json']

export async function cancelTerminalPayment(data: CancelTerminalPaymentRequest): Promise<CancelTerminalPaymentResponse> {
  return request.post<CancelTerminalPaymentResponse, CancelTerminalPaymentRequest>('/api/payments/v2/stripe/terminal/cancel-payment', data, { credentials: 'include' })
}
